import { RefObject, useMemo, useState } from 'react';
import clsx from 'clsx';
import { formatMoney } from '@belong/common';
import { Divider, Text } from '@belong/ui';
import Collapsible from 'components/Collapsible/Collapsible';
import {
  MaintenanceQuoteConsumerModel,
  QuoteTypeOfWorkLabels,
} from 'maintenance/types/quote-centric';
import { isQuoteApproved, isQuoteRejected } from 'maintenance/utils';
import { QuoteDetails } from './QuoteDetails';

type QuoteCentricSectionProps = {
  maintenanceUniqueId: string;
  quotes: MaintenanceQuoteConsumerModel[];
  onQuotesChange: () => Promise<void>;
  sectionRef?: RefObject<HTMLDivElement>;
};

type QuoteGroup = {
  lead: MaintenanceQuoteConsumerModel;
  quotes: MaintenanceQuoteConsumerModel[];
};

type GroupInfo = {
  priceRange: string | undefined;
  hasPendingConsent: boolean;
};

const myAmountForQuote = (q: MaintenanceQuoteConsumerModel): number =>
  q.myApproval?.estimatedAmount ?? q.myEstimatedAmount ?? 0;

const formatAmountRange = (min: number, max: number): string => {
  if (max === 0) return 'FREE';
  if (min === max) return String(formatMoney(min));
  if (min === 0) return `FREE – ${formatMoney(max)}`;
  return `${formatMoney(min)} – ${formatMoney(max)}`;
};

const hasOtherPartyPending = (q: MaintenanceQuoteConsumerModel) =>
  q.approvals.some(
    (a) => a.consentStatus === 'Required' && a.party !== q.myApproval?.party,
  );

type GroupStatus =
  | 'Approved'
  | 'ApprovalRequired'
  | 'PendingApproval'
  | 'Rejected';

const computeGroupStatus = (
  groupQuotes: MaintenanceQuoteConsumerModel[],
): GroupStatus | undefined => {
  if (groupQuotes.some(isQuoteApproved)) return 'Approved';
  if (groupQuotes.some((q) => q.myApproval?.consentStatus === 'Required'))
    return 'ApprovalRequired';
  if (groupQuotes.some(hasOtherPartyPending)) return 'PendingApproval';
  if (groupQuotes.length > 0 && groupQuotes.every(isQuoteRejected))
    return 'Rejected';
  return undefined;
};

const computeGroupInfo = (
  groupQuotes: MaintenanceQuoteConsumerModel[],
): GroupInfo => {
  let priceRange: string | undefined;
  if (groupQuotes.length > 0) {
    const approved = groupQuotes.find(isQuoteApproved);
    if (approved) {
      const amount = myAmountForQuote(approved);
      priceRange = formatAmountRange(amount, amount);
    } else {
      const amounts = groupQuotes.map(myAmountForQuote);
      priceRange = formatAmountRange(
        Math.min(...amounts),
        Math.max(...amounts),
      );
    }
  }
  const hasPendingConsent = groupQuotes.some(
    (q) => q.myApproval?.consentStatus === 'Required',
  );
  return { priceRange, hasPendingConsent };
};

type QuoteTabOption = {
  value: string;
  name: string;
  subtitle: string;
  tag?: string;
  tagClassName?: string;
};

const buildTabOptions = (
  groupQuotes: MaintenanceQuoteConsumerModel[],
): QuoteTabOption[] => {
  return groupQuotes.map((quote, index) => {
    const isRejected = isQuoteRejected(quote);
    const isApproved = !isRejected && isQuoteApproved(quote);
    const isPendingOther =
      !isApproved && !isRejected && hasOtherPartyPending(quote);

    const tag = isRejected
      ? 'Rejected'
      : isApproved
        ? 'Approved'
        : isPendingOther
          ? 'Pending'
          : quote.isRecommended
            ? 'Recommended'
            : undefined;

    const tagClassName = isRejected
      ? 'bg-dark-gray'
      : isPendingOther
        ? 'bg-red'
        : undefined;

    const description = quote.title || quote.assignedPro?.name || '';
    const name = description || `Option ${index + 1}`;
    const workLabel = quote.typeOfWork
      ? QuoteTypeOfWorkLabels[quote.typeOfWork]
      : undefined;
    const myAmount = myAmountForQuote(quote);
    const formattedTotal =
      myAmount === 0 ? 'Free' : String(formatMoney(myAmount) ?? '');
    const subtitle = workLabel
      ? `${workLabel} • ${formattedTotal}`
      : formattedTotal;

    return { value: quote.uniqueId ?? '', name, subtitle, tag, tagClassName };
  });
};

type QuoteOptionsSelectorProps = {
  options: QuoteTabOption[];
  value: string | undefined;
  onChange: (value: string) => void;
};

const QuoteOptionsSelector = ({
  options,
  value,
  onChange,
}: QuoteOptionsSelectorProps) => (
  <div className="no-scrollbar w-full overflow-x-auto">
    <div className="flex w-max flex-nowrap items-stretch gap-xs px-sm py-xs">
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <div
            key={option.value}
            className="relative min-w-[175px] max-w-[250px] shrink-0"
          >
            {option.tag && (
              <span
                className={clsx(
                  'absolute inset-x-0 z-10 mx-auto -mt-xs w-min whitespace-nowrap rounded-small px-xs text-p1 text-white',
                  option.tagClassName ?? 'bg-green',
                )}
              >
                {option.tag}
              </span>
            )}
            <button
              type="button"
              onClick={() => onChange(option.value)}
              className={clsx(
                'mt-2xs flex h-full w-full flex-col items-center justify-center rounded border border-solid border-gray p-xs',
                isSelected && 'border-2 border-navy bg-white',
              )}
            >
              <Text className="text-center font-semibold">{option.name}</Text>
              <Text variant="p1">{option.subtitle}</Text>
            </button>
          </div>
        );
      })}
    </div>
  </div>
);

const getDefaultIndex = () => 0;

type QuoteGroupContentProps = {
  groupQuotes: MaintenanceQuoteConsumerModel[];
  maintenanceUniqueId: string;
  onQuotesChange: () => Promise<void>;
  hasMultipleQuotes: boolean;
};

const QuoteGroupContent = ({
  groupQuotes,
  maintenanceUniqueId,
  onQuotesChange,
  hasMultipleQuotes,
}: QuoteGroupContentProps) => {
  const [activeQuoteId, setActiveQuoteId] = useState<string | null>(null);

  const activeIndex = useMemo(() => {
    if (activeQuoteId) {
      const idx = groupQuotes.findIndex((q) => q.uniqueId === activeQuoteId);
      if (idx >= 0) return idx;
    }
    return getDefaultIndex();
  }, [activeQuoteId, groupQuotes]);

  const activeQuote = groupQuotes[activeIndex];
  const options = useMemo(() => buildTabOptions(groupQuotes), [groupQuotes]);

  return (
    <div>
      {groupQuotes.length > 1 && (
        <div className="my-sm">
          <QuoteOptionsSelector
            options={options}
            value={activeQuote?.uniqueId}
            onChange={setActiveQuoteId}
          />
        </div>
      )}
      {activeQuote && (
        <QuoteDetails
          key={activeQuote.uniqueId}
          maintenanceUniqueId={maintenanceUniqueId}
          quote={activeQuote}
          onQuotesChange={onQuotesChange}
          hasMultipleQuotes={hasMultipleQuotes}
        />
      )}
    </div>
  );
};

const GROUP_STATUS_LABEL: Record<GroupStatus, string> = {
  Approved: 'Approved',
  ApprovalRequired: 'Approval Required',
  PendingApproval: 'Pending Approval',
  Rejected: 'Rejected',
};

const GROUP_STATUS_CLASS: Record<GroupStatus, string> = {
  Approved: 'text-green',
  ApprovalRequired: 'text-red',
  PendingApproval: 'text-red',
  Rejected: 'text-dark-gray',
};

type GroupCollapsibleTitleProps = {
  title: string;
  priceRange?: string;
  status?: GroupStatus;
};

const GroupCollapsibleTitle = ({
  title,
  priceRange,
  status,
}: GroupCollapsibleTitleProps) => (
  <div className="flex flex-1 items-center justify-between pr-sm">
    <div>
      <Text fontWeight="semibold">{title}</Text>
      {status && (
        <Text
          variant="p1"
          fontWeight="semibold"
          className={GROUP_STATUS_CLASS[status]}
        >
          {GROUP_STATUS_LABEL[status]}
        </Text>
      )}
    </div>
    {priceRange && (
      <Text fontWeight="semibold" className="whitespace-nowrap">
        {priceRange}
      </Text>
    )}
  </div>
);

const quoteStatusPriority = (quote: MaintenanceQuoteConsumerModel): number => {
  if (quote.status === 'Rejected' || quote.status === 'NotSelected') return 2;
  if (quote.status === 'Approved') return 1;
  if (quote.status === 'Published') return 0;
  return 3;
};

const sortQuotesByStatusAndRecommended = (
  a: MaintenanceQuoteConsumerModel,
  b: MaintenanceQuoteConsumerModel,
): number => {
  const priorityDiff = quoteStatusPriority(a) - quoteStatusPriority(b);
  if (priorityDiff !== 0) return priorityDiff;
  if (a.isRecommended && !b.isRecommended) return -1;
  if (!a.isRecommended && b.isRecommended) return 1;
  return 0;
};

export const QuoteCentricSection = ({
  maintenanceUniqueId,
  quotes,
  onQuotesChange,
  sectionRef,
}: QuoteCentricSectionProps) => {
  const mainWorkGroupQuotes = useMemo(() => {
    const lead = quotes.find((q) => !q.quoteType);
    return quotes
      .filter(
        (q) =>
          !q.quoteType ||
          (q.quoteType === 'SecondOpinion' &&
            q.parentQuoteUniqueId === lead?.uniqueId),
      )
      .sort(sortQuotesByStatusAndRecommended);
  }, [quotes]);

  const followUpGroups = useMemo<QuoteGroup[]>(() => {
    const leads = quotes
      .filter((q) => q.quoteType === 'FollowUpWork')
      .sort(sortQuotesByStatusAndRecommended);
    return leads.map((lead) => ({
      lead,
      quotes: [
        lead,
        ...quotes
          .filter(
            (q) =>
              q.quoteType === 'SecondOpinion' &&
              q.parentQuoteUniqueId === lead.uniqueId,
          )
          .sort(sortQuotesByStatusAndRecommended),
      ],
    }));
  }, [quotes]);

  const mainGroupInfo = useMemo(
    () => computeGroupInfo(mainWorkGroupQuotes),
    [mainWorkGroupQuotes],
  );
  const mainGroupStatus = useMemo(
    () => computeGroupStatus(mainWorkGroupQuotes),
    [mainWorkGroupQuotes],
  );
  const followUpGroupInfos = useMemo(
    () => followUpGroups.map((g) => computeGroupInfo(g.quotes)),
    [followUpGroups],
  );
  const followUpGroupStatuses = useMemo(
    () => followUpGroups.map((g) => computeGroupStatus(g.quotes)),
    [followUpGroups],
  );

  const combinedTotal = useMemo(() => {
    if (mainWorkGroupQuotes.length === 0 || followUpGroups.length === 0)
      return undefined;

    const hasPending =
      mainGroupInfo.hasPendingConsent ||
      followUpGroupInfos.some((d) => d.hasPendingConsent);

    if (!hasPending) {
      const mainApproved = mainWorkGroupQuotes.find(isQuoteApproved);
      const fuApproved = followUpGroups.map((g) =>
        g.quotes.find(isQuoteApproved),
      );
      if (!mainApproved || fuApproved.some((q) => !q)) return undefined;
      const total =
        myAmountForQuote(mainApproved) +
        fuApproved.reduce((s, q) => s + (q ? myAmountForQuote(q) : 0), 0);
      return total === 0 ? 'FREE' : String(formatMoney(total));
    }

    const groupBounds = (
      quotes: MaintenanceQuoteConsumerModel[],
    ): { min: number; max: number } | null => {
      if (quotes.length === 0) return null;
      const approved = quotes.find(isQuoteApproved);
      if (approved) {
        const amount = myAmountForQuote(approved);
        return { min: amount, max: amount };
      }
      const amounts = quotes.map(myAmountForQuote);
      return { min: Math.min(...amounts), max: Math.max(...amounts) };
    };

    const mainBounds = groupBounds(mainWorkGroupQuotes);
    const fuBounds = followUpGroups.map((g) => groupBounds(g.quotes));
    if (!mainBounds || fuBounds.some((b) => !b)) return undefined;
    const min =
      mainBounds.min + fuBounds.reduce((s, b) => s + (b?.min ?? 0), 0);
    const max =
      mainBounds.max + fuBounds.reduce((s, b) => s + (b?.max ?? 0), 0);
    return formatAmountRange(min, max);
  }, [mainWorkGroupQuotes, followUpGroups, mainGroupInfo, followUpGroupInfos]);

  const hasPendingConsentAnywhere =
    mainGroupInfo.hasPendingConsent ||
    followUpGroupInfos.some((d) => d.hasPendingConsent);

  if (quotes.length === 0) return null;

  const mainWorkTitle =
    mainWorkGroupQuotes.find((q) => !q.quoteType && q.title)?.title ||
    mainWorkGroupQuotes.find((q) => q.title)?.title ||
    'Main Work';

  const inner = (
    <div className="pt-sm">
      {mainWorkGroupQuotes.length > 0 && (
        <>
          {mainWorkGroupQuotes.length > 1 || followUpGroups.length > 0 ? (
            <div className="mb-sm rounded border border-solid border-gray bg-light-gray">
              <Collapsible
                titleCustomClassName="items-center p-sm"
                titleComponent={
                  <GroupCollapsibleTitle
                    title={mainWorkTitle}
                    priceRange={mainGroupInfo.priceRange}
                    status={mainGroupStatus}
                  />
                }
                title={mainWorkTitle}
                collapsed={!mainGroupInfo.hasPendingConsent}
                inner={
                  <QuoteGroupContent
                    groupQuotes={mainWorkGroupQuotes}
                    maintenanceUniqueId={maintenanceUniqueId}
                    onQuotesChange={onQuotesChange}
                    hasMultipleQuotes
                  />
                }
              />
            </div>
          ) : (
            <QuoteGroupContent
              groupQuotes={mainWorkGroupQuotes}
              maintenanceUniqueId={maintenanceUniqueId}
              onQuotesChange={onQuotesChange}
              hasMultipleQuotes={false}
            />
          )}
        </>
      )}

      {followUpGroups.map((group, groupIndex) => {
        const info = followUpGroupInfos[groupIndex];
        if (!info) return null;
        return (
          <div
            key={group.lead.uniqueId}
            className="mb-sm rounded border border-solid border-gray bg-light-gray"
          >
            <Collapsible
              titleCustomClassName="items-center p-sm"
              titleComponent={
                <GroupCollapsibleTitle
                  title={group.lead.title || 'Follow-Up Work'}
                  priceRange={info.priceRange}
                  status={followUpGroupStatuses[groupIndex]}
                />
              }
              title="Follow-Up Work"
              collapsed={!info.hasPendingConsent}
              inner={
                <QuoteGroupContent
                  groupQuotes={group.quotes}
                  maintenanceUniqueId={maintenanceUniqueId}
                  onQuotesChange={onQuotesChange}
                  hasMultipleQuotes
                />
              }
            />
          </div>
        );
      })}

      {combinedTotal && (
        <>
          <Divider borderStyle="dashed" className="my-sm" />
          <div className="flex justify-between py-xs">
            <Text fontWeight="semibold">
              {hasPendingConsentAnywhere ? 'Estimated Total' : 'Total'}
            </Text>
            <Text fontWeight="semibold">{combinedTotal}</Text>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div ref={sectionRef} className="py-sm">
      <Collapsible
        title="Proposals"
        collapsed={!hasPendingConsentAnywhere}
        inner={inner}
      />
    </div>
  );
};
